import React, { ChangeEvent } from 'react';
import { useField } from 'formik';
import styled, { CSSProperties } from 'styled-components/macro';
import { ImCheckmark } from 'react-icons/im';
import { FormikLabel } from './FormikLabel';
import { Flex, FlexProps } from '../styles/layout';

const CheckboxInput = styled.input`
  position: absolute;
  top: 0px;
  bottom: 0;
  opacity: 0;
  left: 0;
  right: 0;
  cursor: pointer;
`;

const StyledCheckbox = styled.div<{
  checked: boolean;
  disabled?: boolean;
  checkGroupIndicator?: boolean;
}>`
  width: 20px;
  height: 20px;

  ${({ checkGroupIndicator, checked, theme }) =>
    checkGroupIndicator
      ? `
        background-color: ${checked ? theme.colors.neutral[60] : theme.colors.neutral[20]};
        border: 1px solid ${checked ? theme.colors.neutral[60] : theme.colors.neutral[40]};
      `
      : `
        background-color: ${checked ? theme.colors.primary[100] : theme.colors.neutral[20]};
        border: 1px solid ${checked ? theme.colors.primary[100] : theme.colors.neutral[40]};
      `}

  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms;

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  svg {
    visibility: ${({ checked }) => (checked ? 'visible' : 'hidden')};
    color: white;
  }
`;

const Icon = styled.svg`
  fill: none;
  stroke: white;
  stroke-width: 5px;
  display: flex;
`;

export type StyleObject = { wrapper?: FlexProps; labelStyles?: CSSProperties };

interface CustomCheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  checkGroupIndicator?: boolean;
  extendStyles?: StyleObject;
}

export const FormikCheckboxField = ({
  label,
  extendStyles,
  onChange,
  checkGroupIndicator,
  ...props
}: CustomCheckboxProps) => {
  const [field, _, helpers] = useField({ ...props, type: 'checkbox' });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) {
      return;
    }
    if (onChange) {
      return onChange(e);
    }
    helpers.setValue(!field.value);
  };

  return (
    <Flex
      flexDirection='row'
      alignItems='center'
      gap='4px'
      style={{ position: 'relative' }}
      width='auto'
      {...extendStyles?.wrapper}
    >
      <CheckboxInput
        {...field}
        {...props}
        id={props.name}
        type='checkbox'
        onChange={handleChange}
      />
      <StyledCheckbox
        checked={
          props.checked !== undefined ? !!props.checked : !!field.checked
        }
        disabled={props.disabled}
        checkGroupIndicator={checkGroupIndicator}
      >
        {checkGroupIndicator ? (
          <Icon viewBox='0 0 24 24'>
            <polyline points='4,12 20,12' />
          </Icon>
        ) : (
          <ImCheckmark size={16} />
        )}
      </StyledCheckbox>

      {label && (
        <FormikLabel
          label={label}
          name={props.name}
          isRequired={false}
          disabled={!!props.disabled}
          labelStyles={extendStyles?.labelStyles}
        />
      )}
    </Flex>
  );
};
